import type { CustomPageSectionDraft, RestaurantNavLinkDraft } from '~/libs/gql/types';
import {
  TOGGLE_IFRAME_RENDER,
  SET_LOADING_PREVIEW,
  SET_SELECTED_PAGE,
  SET_PREVIEW_SELECTED_SECTION,
  SET_ACTIVE_SECTION,
  SET_ADD_SECTION_OPENED,
  SET_FORM_ERROR,
  type WebBuilderAction,
} from './WebBuilderActions';

export interface FormValidationResult {
  invalidFields: string[];
  messages: string[];
}

export type AddSectionOpenedState =
  | { isOpen: false }
  | { isOpen: true; position?: number };

export interface ImmutableCustomSection {
  id?: undefined;
  isCustomSection: true;
  navItem?: RestaurantNavLinkDraft | null;
  sectionType: string;
}

export type Section = DeepPartial<CustomPageSectionDraft> & { isCustomSection?: false };

export type PreviewSelectedSection = number | ImmutableCustomSection | null;

export type PreviewSelectedSectionUpdate = {
  previewSelectedSection: PreviewSelectedSection;
  selectedPageId?: number | null;
};

export interface WebBuilderState {
  activeSection: Section | ImmutableCustomSection | null;
  addSectionOpened: AddSectionOpenedState;
  formError: FormValidationResult | null;
  loadingPreview: boolean;
  pageUrl: string | null;
  previewSelectedSection: PreviewSelectedSection;
  selectedPageId: number | null;
  toggleRender: number;
}

export const initialWebBuilderState: WebBuilderState = {
  activeSection: null,
  addSectionOpened: { isOpen: false },
  formError: null,
  loadingPreview: true,
  pageUrl: null,
  previewSelectedSection: null,
  selectedPageId: null,
  toggleRender: 0,
};

export const isImmutableCustomSection = (
  section: Section | PreviewSelectedSection | undefined,
): section is ImmutableCustomSection => (
  typeof section === 'object' && section != null && section.isCustomSection === true
);

const WebBuilderReducer = (state: WebBuilderState = initialWebBuilderState, action: WebBuilderAction) => {
  switch (action.type) {
    case TOGGLE_IFRAME_RENDER:
      return {
        ...state,
        toggleRender: state.toggleRender === 0 ? 1 : 0,
      };

    case SET_LOADING_PREVIEW:
      return {
        ...state,
        loadingPreview: action.loadingPreview,
      };

    case SET_SELECTED_PAGE:
      return {
        ...state,
        ...(action.selectedPageId !== state.selectedPageId || action.selectedPageId === null ? {
          activeSection: null,
          addSectionOpened: { isOpen: false as const },
          previewSelectedSection: null,
        } : {}),
        pageUrl: action.pageUrl,
        selectedPageId: action.selectedPageId,
      };

    case SET_PREVIEW_SELECTED_SECTION:
      return {
        ...state,
        ...(action.data.selectedPageId !== undefined && action.data.selectedPageId !== state.selectedPageId ? {
          activeSection: null,
          addSectionOpened: { isOpen: false as const },
        } : {}),
        previewSelectedSection: action.data.previewSelectedSection,
        ...(action.data.selectedPageId === undefined ? {} : { selectedPageId: action.data.selectedPageId }),
      };
    case SET_ACTIVE_SECTION:
      return {
        ...state,
        activeSection: action.section,
      };
    case SET_ADD_SECTION_OPENED:
      return {
        ...state,
        addSectionOpened: action.payload,
      };
    case SET_FORM_ERROR:
      return {
        ...state,
        formError: action.formError,
      };

    default:
      return state;
  }
};

export default WebBuilderReducer;
